/* Todaysunny 3.8 — distinct checkout and add-to-cart controls, same current variant. */
if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      if (!this.form) return;
      if (this.variantIdInput) this.variantIdInput.disabled = false;
      this.submitButton = this.querySelector('[name="add"]');
      this.buyNowButton = this.querySelector('[data-ts-buy-now]');
      this.submitButtonText = this.submitButton?.querySelector('span');
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (document.querySelector('cart-drawer')) this.submitButton?.setAttribute('aria-haspopup', 'dialog');
      this.hideErrors = this.dataset.hideErrors === 'true';
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
    }

    connectedCallback() {
      this.lifecycle?.abort();
      this.lifecycle = new AbortController();
      window.addEventListener('pageshow', () => this.resetPending(), { signal: this.lifecycle.signal });
      window.addEventListener('pagehide', () => clearTimeout(this.navigationTimer), { signal: this.lifecycle.signal });
    }

    disconnectedCallback() { this.lifecycle?.abort(); clearTimeout(this.navigationTimer); }

    onSubmitHandler(evt) {
      evt.preventDefault();
      const direct = !!evt.submitter?.matches('[data-ts-buy-now]');
      const button = direct ? this.buyNowButton : this.submitButton;
      if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true' || this.pendingAction) return;
      const guard = this.closest('product-info')?.tsSizeGuard;
      if (guard && !guard.allowSubmit()) return;
      this.handleErrorMessage();
      if (!this.form.reportValidity()) return;
      const formData = new FormData(this.form);
      const quantityInput = this.form.elements.namedItem('quantity');
      const quantity = formData.get('quantity') ?? '1';
      const rules = { min: (direct ? quantityInput?.dataset.min : null) || quantityInput?.getAttribute('min'), max: (direct ? quantityInput?.dataset.max : null) || quantityInput?.getAttribute('max'), step: quantityInput?.getAttribute('step') };
      if (window.TSCheckout && !window.TSCheckout.validQuantity(quantity, rules)) {
        this.handleErrorMessage(window.tsStrings?.quantityError || 'Choose a valid quantity for this option.');
        quantityInput?.focus();
        return;
      }
      if (!/^\d+$/.test(String(formData.get('id') || ''))) {
        this.handleErrorMessage(window.tsStrings?.variantError || 'Choose an available product option.');
        return;
      }
      if (direct) this.startDirectCheckout(formData, button);
      else this.addToCart(formData, button, evt);
    }

    setPending(action, button) {
      this.pendingAction = action;
      this.activeButton = button;
      [this.submitButton, this.buyNowButton].filter(Boolean).forEach((control) => control.setAttribute('aria-disabled', 'true'));
      button.setAttribute('aria-busy', 'true');
      button.classList.add('loading');
      button.querySelector('.loading__spinner')?.classList.remove('hidden');
      this.closest('product-info')?.dispatchEvent(new CustomEvent('ts:purchase-state'));
    }

    resetPending() {
      clearTimeout(this.navigationTimer);
      this.pendingAction = null;
      [this.submitButton, this.buyNowButton].filter(Boolean).forEach((button) => {
        button.classList.remove('loading');
        button.removeAttribute('aria-busy');
        button.removeAttribute('aria-disabled');
        button.querySelector('.loading__spinner')?.classList.add('hidden');
      });
      this.closest('product-info')?.dispatchEvent(new CustomEvent('ts:purchase-state'));
    }

    startDirectCheckout(formData, button) {
      try {
        if (!window.TSCheckout) throw new Error(window.tsStrings?.checkoutError || 'Checkout is not ready. Please reload the page or use Add to cart.');
        const properties = Object.create(null);
        for (const [name, value] of formData.entries()) {
          const match = name.match(/^properties\[([^\]]+)\]$/);
          if (!match) continue;
          if (typeof value !== 'string') {
            if (value.size) throw new Error('Please use Add to cart for products with uploaded files.');
            continue;
          }
          properties[match[1]] = value;
        }
        const url = window.TSCheckout.buildCheckoutURL({
          origin: window.location.origin,
          cartPath: window.routes?.cart_url || '/cart',
          variantId: formData.get('id'),
          quantity: formData.get('quantity') ?? '1',
          locale: window.Shopify?.locale || document.documentElement.lang || '',
          sellingPlan: formData.get('selling_plan') || '',
          properties,
        });
        this.setPending('checkout', button);
        // A navigation watchdog only restores the UI if this document stays open.
        this.navigationTimer = setTimeout(() => {
          if (this.pendingAction === 'checkout') {
            this.resetPending();
            this.handleErrorMessage(window.tsStrings?.checkoutError || 'Checkout did not open. Please try again or use Add to cart.');
          }
        }, 15000);
        this.dispatchEvent(new CustomEvent('ts:buy-now', { bubbles: true, detail: { variantId: String(formData.get('id')), quantity: Number(formData.get('quantity') || 1) } }));
        window.location.assign(url);
      } catch (error) {
        this.resetPending();
        this.handleErrorMessage(error.message || window.tsStrings?.checkoutError || 'Unable to open checkout. Please try again.');
      }
    }

    addToCart(formData, button, evt) {
      this.failedRequest = false;
      this.error = false;
      this.setPending('cart', button);
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }
      config.body = formData;
      fetch(`${routes.cart_add_url}`, config)
        .then(async (response) => {
          const body = await response.json();
          if (!response.ok && !body.status) body.status = response.status;
          return body;
        })
        .then((response) => {
          if (response.status) {
            this.failedRequest = true;
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form', productVariantId: formData.get('id'),
              errors: response.errors || response.description, message: response.message,
            });
            this.handleErrorMessage(response.description || response.message || window.cartStrings?.error || 'We could not update your shopping bag. Please try again.');
            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (soldOutMessage) {
              this.submitButtonText?.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
            }
            return;
          }
          if (!this.cart) { window.location.assign(window.routes.cart_url); return; }
          const startMarker = typeof CartPerformance !== 'undefined' ? CartPerformance.createStartingMarker('add:wait-for-subscribers') : null;
          Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form', productVariantId: formData.get('id'), cartData: response,
          })).then(() => {
            if (startMarker) CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
          });
          const renderCart = () => {
            if (typeof CartPerformance !== 'undefined') CartPerformance.measure('add:paint-updated-sections', () => this.cart.renderContents(response));
            else this.cart.renderContents(response);
          };
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => setTimeout(renderCart), { once: true });
            quickAddModal.hide(true);
          } else renderCart();
        })
        .catch((error) => {
          this.failedRequest = true;
          this.handleErrorMessage(window.cartStrings?.error || 'We could not update your shopping bag. Please check your bag before trying again.');
          console.error(error);
        })
        .finally(() => {
          if (!this.failedRequest && this.cart?.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          this.resetPending();
          if (this.error) this.submitButton.setAttribute('aria-disabled', 'true');
          if (typeof CartPerformance !== 'undefined') CartPerformance.measureFromEvent('add:user-action', evt);
        });
    }

    handleErrorMessage(errorMessage = false) {
      // Gift-card component owns its recipient errors; checkout/cart errors still need a visible status.
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
      if (errorMessage) {
        this.errorMessage.textContent = typeof errorMessage === 'string' ? errorMessage : (window.cartStrings?.error || 'Please review your selection and try again.');
        this.errorMessageWrapper.setAttribute('tabindex', '-1');
        this.errorMessageWrapper.focus({ preventScroll: true });
      }
    }

    toggleSubmitButton(disable = true, text) {
      if (!this.submitButton) return;
      this.submitButton.toggleAttribute('disabled', disable);
      this.buyNowButton?.toggleAttribute('disabled', disable);
      if (this.submitButtonText) this.submitButtonText.textContent = disable && text ? text : (window.variantStrings?.addToCart || 'Add to cart');
      const buyText = this.buyNowButton?.querySelector('span');
      if (buyText) buyText.textContent = disable && text ? text : (window.tsStrings?.buyNow || 'Buy now');
      this.closest('product-info')?.dispatchEvent(new CustomEvent('ts:purchase-state'));
    }

    get variantIdInput() { return this.form?.querySelector('[name=id]'); }
  });
}
